import { useEffect, useRef, useState, type ReactNode } from 'react';
import maplibregl, {
  type Map,
  type MapOptions,
  AttributionControl,
  FullscreenControl,
  GeolocateControl,
  NavigationControl,
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { CARTO_VOYAGER_STYLE, getMapStyleUrl } from '@/utils/mapConfig';
import { MapContext } from './MapContext';

export interface MapLibreMapProps {
  /** MapLibre center as [lng, lat]. */
  center?: [number, number];
  zoom?: number;
  style?: string;
  accessToken?: string;
  children?: ReactNode;
  onMapLoad?: (map: Map) => void;
  onMapClick?: (lng: number, lat: number) => void;
  className?: string;
  showControls?: boolean;
  showGeolocate?: boolean;
  showFullscreen?: boolean;
}

export function MapLibreMap({
  center = [35.3308, 36.9914],
  zoom = 13,
  style,
  accessToken,
  children,
  onMapLoad,
  onMapClick,
  className = '',
  showControls = true,
  showGeolocate = false,
  showFullscreen = false,
}: MapLibreMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);
  const onMapLoadRef = useRef(onMapLoad);
  const onMapClickRef = useRef(onMapClick);
  const initialCenterRef = useRef(center);
  const initialZoomRef = useRef(zoom);
  const [mapInstance, setMapInstance] = useState<Map | null>(null);

  useEffect(() => {
    onMapLoadRef.current = onMapLoad;
  }, [onMapLoad]);

  useEffect(() => {
    onMapClickRef.current = onMapClick;
  }, [onMapClick]);

  useEffect(() => {
    if (!mapContainerRef.current) {
      return;
    }

    const styleUrl = style ?? getMapStyleUrl(accessToken);

    const mapOptions: MapOptions = {
      container: mapContainerRef.current,
      style: styleUrl,
      center: initialCenterRef.current,
      zoom: initialZoomRef.current,
    };

    const map = new maplibregl.Map(mapOptions);

    const notifyLoad = () => {
      setMapInstance(map);
      onMapLoadRef.current?.(map);
    };

    map.on('load', notifyLoad);

    map.on('error', (e) => {
      if (e.error?.message?.includes('unknown property')) {
        try {
          map.setStyle(CARTO_VOYAGER_STYLE);
        } catch {
          // ignore fallback errors
        }
      }
    });

    map.on('styledata', () => {
      if (map.isStyleLoaded()) {
        notifyLoad();
      }
    });

    if (onMapClickRef.current) {
      map.on('click', (e) => {
        onMapClickRef.current?.(e.lngLat.lng, e.lngLat.lat);
      });
    }

    map.addControl(new AttributionControl({ compact: true }), 'bottom-right');

    if (showControls) {
      map.addControl(new NavigationControl(), 'top-right');
    }

    if (showGeolocate) {
      map.addControl(
        new GeolocateControl({
          positionOptions: { enableHighAccuracy: true },
          trackUserLocation: true,
        }),
        'top-right',
      );
    }

    if (showFullscreen) {
      map.addControl(new FullscreenControl(), 'top-right');
    }

    mapRef.current = map;

    return () => {
      setMapInstance(null);
      map.remove();
      mapRef.current = null;
    };
  }, [style, accessToken, showControls, showGeolocate, showFullscreen]);

  useEffect(() => {
    if (!mapRef.current?.isStyleLoaded()) {
      return;
    }

    const currentCenter = mapRef.current.getCenter();
    const centerChanged =
      Math.abs(currentCenter.lng - center[0]) > 0.0001 ||
      Math.abs(currentCenter.lat - center[1]) > 0.0001;

    if (centerChanged) {
      mapRef.current.setCenter(center);
    }
  }, [center]);

  useEffect(() => {
    if (!mapRef.current?.isStyleLoaded() || zoom === undefined) {
      return;
    }

    if (Math.abs(mapRef.current.getZoom() - zoom) > 0.1) {
      mapRef.current.setZoom(zoom);
    }
  }, [zoom]);

  return (
    <div ref={mapContainerRef} className={`h-full w-full ${className}`}>
      <MapContext.Provider value={mapInstance}>{children}</MapContext.Provider>
    </div>
  );
}
